import { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Hidden,
  IconButton,
  Toolbar,
  Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoDashboard from './LogoDashboard';

import { getCurrentUser, logout } from "../utils/auth"
import getInitials from '../utils/getInitials';

import { itemsMenu } from "./DashboardSidebar"
import MyAccountModal from "./ui/MyAccountModal"
import UpgradeModal from "./ui/UpgradeModal"
import GlobalSearch from "./GlobalSearch"
import NotificationsBell from "./NotificationsBell"

import accountIcon from "../images/accountIcon.svg"
import pixelButtonIcon from "../images/pixelButtonIcon.svg"
import chevronDownIcon from "../images/chevronDownIcon.svg"
import logOutIcon from "../images/logOutIcon.svg"

// S1 : le lien "Compte" de la navbar est remplace par un dropdown sur le profil
// (meme design que GlobalMenu de THE LAB). Le dropdown ouvre la modale
// "Mon compte" (S2) ou deconnecte.

const DashboardNavbar = ({ onMobileNavOpen, ...rest }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentUser = getCurrentUser()
  // La barre du haut de la maquette (menus, recherche, notifications, Upgrade)
  // est un outil de RECRUTEUR. Un candidat ne doit voir que son profil.
  const estRecruteur = currentUser?.type === "theScanRecruiter"

  const theme = useTheme();
  const mediaQueryUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const mediaQueryUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [globalMenu, setGlobalMenu] = useState(false)
  const [accountModal, setAccountModal] = useState(false)
  const [upgradeModal, setUpgradeModal] = useState(false)
  const globalMenuRef = useRef(null)

  // fermeture du dropdown au clic exterieur
  useEffect(() => {
    const onClickOutside = (e) => {
      if (globalMenuRef.current && !globalMenuRef.current.contains(e.target)) {
        setGlobalMenu(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => { document.removeEventListener("mousedown", onClickOutside) }
  }, [])

  const itemMenuSx = {
    height:"50px",
    padding:"15px 20px",
    lineHeight:"20px",
    fontSize:"16px",
    color:"text.black",
    cursor:"pointer",
    display:"flex",
    gap:"10px",
    "&:hover" : {
      backgroundColor:"#F7F7F7",
    }
  }

  return (
    <AppBar
      elevation={0}
      sx={{background:"#FFFFFF", borderBottom:"1px solid black"}}
      {...rest}
    >
      <Toolbar sx={{height: "64px !important", minHeight: "64px !important", background:"#FFFFFF",}}>
        <Box sx={{
          display:"flex",
          alignItems:"center",
          height:"100%"
        }}>
          <IconButton onClick={onMobileNavOpen} size="small" sx={{height:"34px", visibility: mediaQueryUpLg ? "hidden" : "visible"}}>
            <MenuIcon />
          </IconButton>
          <RouterLink to="/" style={{marginLeft:"15px", marginRight:"15px", display:"flex", alignItems:"center"}}>
            <LogoDashboard />
          </RouterLink>
          <Hidden lgDown>
            <Box sx={{display:"flex", marginLeft:"25px", marginRight:"25px"}}>
              {itemsMenu.filter((i) => estRecruteur || !i.recruteurSeulement).map((item, index) => {
                const active = item?.href ? !!matchPath({
                  path: item?.href,
                  end: false
                }, location.pathname) : false;
                const styleLien = {
                  fontSize:"15px",
                  color:"black",
                  marginLeft:"25px",
                  marginRight:"25px",
                  fontWeight:active ? 600 : 400
                }
                if (item?.externe) {
                  return (
                    <Button
                      key={index}
                      sx={styleLien}
                      size="small"
                      onClick={() => { window.open(item.href, "_blank") }}
                      endIcon={<KeyboardArrowDownIcon/>}
                    >
                      <span>{item?.title}</span>
                    </Button>
                  )
                }
                return (
                  <Button
                    key={index}
                    component={RouterLink}
                    sx={styleLien}
                    size="small"
                    to={item?.href}
                    endIcon={<KeyboardArrowDownIcon/>}
                  >
                    <span>
                      {item?.title}
                    </span>
                  </Button>
                )
              })}
            </Box>
          </Hidden>
        </Box>
        {/* S8 : recherche globale (scans + candidats), encadree comme sur la maquette */}
        {/* recherche et cloche : presentes pour les deux roles (maquette) */}
        <Hidden mdDown>
          <Box sx={{
            flexGrow: 1,
            height:"100%",
            display:"flex",
            alignItems:"center",
            borderLeft:"1px solid black",
            borderRight:"1px solid black",
          }}>
            <GlobalSearch estRecruteur={estRecruteur} />
          </Box>
        </Hidden>
        <Hidden mdUp>
          <Box sx={{ flexGrow: 1 }} />
        </Hidden>

        <Box sx={{display:"flex", alignItems:"center", marginLeft:"18px"}}>
          <NotificationsBell />
        </Box>

        {/* S8 : bouton Upgrade (recruteur uniquement) */}
        <Hidden smDown>
          {estRecruteur &&
          <Button
            onClick={() => { setUpgradeModal(true) }}
            variant="contained"
            sx={{
              marginLeft:"14px",
              height:"40px",
              minHeight:"40px !important",
              backgroundColor:"#0D0D0D",
              fontSize:"15px",
              paddingLeft:"18px",
              paddingRight:"14px",
              "&:hover" : { backgroundColor:"#2F2F2F" },
            }}
            endIcon={<img src={pixelButtonIcon} style={{width:"13px", height:"13px"}} alt=""/>}
          >
            Upgrade
          </Button>
          }
        </Hidden>

        <Box ref={globalMenuRef} sx={{
          display:"flex",
          alignItems:"center",
          height:"100%",
          marginLeft:"10px",
        }}>
          <Button
            disableRipple
            onClick={() => { setGlobalMenu(!globalMenu) }}
            sx={{
              fontSize:"15px",
              color:"text.primary",
              marginLeft:"10px",
              "& .MuiButton-endIcon" : {
                marginLeft:"4px !important",
              },
              "&:hover" : {
                background:"transparent",
                color:"rgba(0,0,0,0.75)"
              }
            }}
            size="small"
            endIcon={<img src={chevronDownIcon} style={{width:"14px", transform:globalMenu === true ? "rotate(180deg)" : "none", transition:"transform .15s"}} alt=""/>} // L26
          >
            <AccountCircleIcon sx={{width:"30px", height:"30px",}}/>
            <Hidden smDown>
              <Box sx={{color:"text.black", fontSize:"15px", marginLeft:"8px", lineHeight:"normal"}}>{currentUser?.name}</Box>
            </Hidden>
            <Hidden smUp>
              <Box sx={{color:"text.black", fontSize:"15px", marginLeft:"8px", lineHeight:"normal"}}>{getInitials(currentUser?.name)}</Box>
            </Hidden>
          </Button>
          {globalMenu === true &&
            <Box sx={{
              position:"absolute",
              width:"200px",
              background:"white",
              top:["65px","75px"],
              right:"20px",
              border:"1px solid black",
              boxShadow: "0px 1px 20px rgba(0, 0, 0, 0.1)",
              userSelect:"none",
              zIndex:1300,
            }}>
              <Box
                onClick={() => {
                  setAccountModal(true)
                  setGlobalMenu(false)
                }}
                sx={{...itemMenuSx, borderBottom:"1px solid #EFEFEF"}}
              >
                <img src={accountIcon} style={{height:"20px", width:"20px"}}/>
                Mon compte
              </Box>
              <Box
                onClick={() => {
                  setGlobalMenu(false)
                  logout(() => navigate("/"))
                }}
                sx={itemMenuSx}
              >
                <img src={logOutIcon} style={{height:"20px", width:"20px"}}/>
                Déconnexion
              </Box>
            </Box>
          }
        </Box>
      </Toolbar>

      <MyAccountModal
        isOpen={accountModal}
        onClose={() => { setAccountModal(false) }}
        currentUser={currentUser}
      />

      <UpgradeModal
        isOpen={upgradeModal}
        onClose={() => { setUpgradeModal(false) }}
        currentUser={currentUser}
      />
    </AppBar>
  );
};

DashboardNavbar.propTypes = {
  onMobileNavOpen: PropTypes.func
};

export default DashboardNavbar;
